import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { db } from '../firebase';
import { useAuthContext } from '../contexts/AuthContext';

const APP_STATUS = {
  pending:    { text: '審査中', color: '#d97706', bg: '#fef3c7' },
  accepted:   { text: '当選',   color: '#16a34a', bg: '#dcfce7' },
  rejected:   { text: '落選',   color: '#dc2626', bg: '#fee2e2' },
  waitlisted: { text: '補欠',   color: '#7c3aed', bg: '#ede9fe' },
};

const EVENT_STATUS = {
  waiting:   { text: '準備中',   color: '#64748b' },
  accepting: { text: '受付中',   color: '#2563eb' },
  matching:  { text: '抽選中',   color: '#d97706' },
  matched:   { text: '結果確定', color: '#16a34a' },
};

const GuestPortalPage = () => {
  const { user, userProfile, loading: contextLoading } = useAuthContext();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    if (contextLoading) return;
    if (!user) { navigate('/login'); return; }

    setLoading(true);
    const unsub = onValue(ref(db, 'events'), (snap) => {
      try {
        const events = snap.val() || {};
        const found = [];

        for (const [eventId, event] of Object.entries(events)) {
          const apps   = event.applications || {};
          const guests = event.guests       || {};

          for (const [appId, app] of Object.entries(apps)) {
            if (app.user_id !== user.uid) continue;

            const hasResults   = !!(event.results?.generated_at);
            const guestRecord  = app.guest_id ? guests[app.guest_id] : null;
            const guestMatched = guestRecord?.status === 'matched';

            let app_status;
            if (hasResults) {
              app_status = guestMatched ? 'accepted' : 'rejected';
            } else {
              app_status = 'pending';
            }

            found.push({
              appId,
              eventId,
              event_name:   event.event_name,
              event_status: event.status,
              app_status,
              submitted_at: app.submitted_at,
              has_results:  hasResults,
            });
          }
        }

        found.sort((a, b) => b.submitted_at - a.submitted_at);
        setApplications(found);
        setError(false);
      } catch (e) {
        console.error(e);
        setError(true);
      } finally {
        setLoading(false);
      }
    }, (err) => {
      console.error(err);
      setError(true);
      setLoading(false);
    });

    return () => unsub();
  }, [user, contextLoading, navigate]);

  if (contextLoading || loading) return (
    <div style={centerStyle}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
      <p style={{ color: '#94a3b8', margin: 0 }}>読み込み中...</p>
    </div>
  );

  const displayName = userProfile?.display_name || userProfile?.vrchat_name || user?.email || '';

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6fb', padding: '32px 16px 64px', display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 580 }}>

        {/* ヘッダー */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#6c63ff', background: 'rgba(108,99,255,0.08)', padding: '4px 12px', borderRadius: 20, marginBottom: 8, border: '1px solid rgba(108,99,255,0.15)' }}>
              <img src="/logo.png" alt="LinkBoard" style={{ height: 14, width: 'auto' }} />
              LinkBoard
            </div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>マイポータル</h1>
            {displayName && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#94a3b8' }}>{displayName}</p>}
          </div>
          <Link to="/account" state={{ from: '/portal' }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', textDecoration: 'none', fontSize: 12, fontWeight: 700, color: '#334155' }}>
            ⚙️ 設定
          </Link>
        </div>

        {/* エラー */}
        {error && (
          <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fca5a5', fontSize: 13, color: '#dc2626' }}>
            データの取得に失敗しました。ページを再読み込みしてください。
          </div>
        )}

        {/* 応募一覧 */}
        <section>
          <h2 style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            応募済みイベント
          </h2>

          {applications.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 14, padding: '48px 24px', textAlign: 'center', border: '1px solid #e8edf5' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📭</div>
              <p style={{ margin: 0, fontSize: 14, color: '#94a3b8' }}>まだイベントに応募していません</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {applications.map(app => {
                const appSt = APP_STATUS[app.app_status] || APP_STATUS.pending;
                // matched でも results 未生成なら抽選中として扱う
                const displayEventStatus = (app.event_status === 'matched' && !app.has_results)
                  ? 'matching'
                  : app.event_status;
                const eventSt = EVENT_STATUS[displayEventStatus] || { text: app.event_status, color: '#64748b' };
                return (
                  <div key={app.appId} style={{ background: '#fff', borderRadius: 16, padding: '18px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #e8edf5' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                      <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {app.event_name}
                      </p>
                      <span style={{ fontSize: 11, padding: '2px 9px', borderRadius: 6, background: appSt.bg, color: appSt.color, fontWeight: 700, flexShrink: 0 }}>
                        {appSt.text}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: eventSt.color }}>
                        {eventSt.text}
                      </span>
                      <span style={{ fontSize: 11, color: '#cbd5e1' }}>·</span>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>
                        応募日 {new Date(app.submitted_at).toLocaleDateString('ja-JP')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <p style={{ marginTop: 40, textAlign: 'center', fontSize: 12, color: '#cbd5e1' }}>
          Powered by{' '}
          <a href={window.location.origin} style={{ color: '#6c63ff', fontWeight: 700, textDecoration: 'none' }}>LinkBoard</a>
        </p>
      </div>
    </div>
  );
};

const centerStyle = {
  minHeight: '100vh', display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center', background: '#f4f6fb',
};

export default GuestPortalPage;
